import { SubmitOptions } from "@remix-run/react";
import React from "react";
import { ReducerAction } from "./reducer.ts";
import { Fieldset } from "./types.ts";
import type { useSubmit } from "@remix-run/react";

export class FieldsManager<T extends Fieldset = Fieldset> {
  constructor(
    public state: T,
    public dispatch: React.Dispatch<ReducerAction<T>>,
    public submit: ReturnType<typeof useSubmit>,
    public tryingSubmit = false,
  ) {}

  toFormData() {
    return fieldsetToFormData(this.state);
  }

  hasErrors(s = this.state) {
    let flag = false;
    for (const field in s) {
      if (s[field].errors && s[field].errors?.length) flag = true;
    }
    return flag;
  }

  get hasAllRequiredFields() {
    return Object.keys(this.state).every((field) => {
      //this is stored on state but is not a field. that might be a bad idea?
      if (field === "hasSubmitted") return true;

      if (this.hasValidation(field, "required")) return this.hasValue(field);
      else return true;
    });
  }

  get isSuccessful() {
    return this.state.hasSubmitted && !this.hasErrors();
  }

  get isFailed() {
    return this.state.hasSubmitted && this.hasErrors();
  }

  hasParam(name: string) {
    return !!(name in this.state);
  }

  get(name: string) {
    if (!(name in this.state)) {
      throw new Error(`FieldsManager has no field named ${name}`);
    }
    return this.state[name];
  }

  getValue(name: string) {
    return this.get(name).value;
  }

  /* does the field require validation, and optionally what type of validation does it require */
  hasValidation(name: string, validationType: "required" | "confirmed") {
    if (!this.state[name].validation) return false;
    else {
      //validation might be undefined, dont know how to write this as a one-liner with type safety
      const v = this.state[name].validation;
      return v && v[validationType];
    }
  }

  getFieldErrors(name: string) {
    return this.state[name].errors;
  }

  getFormErrors() {}

  clearErrors(name: string) {
    this.dispatch({ type: "clearErrors", name: name });
  }

  setValue(name: string, value: string) {
    this.dispatch({ type: "setValue", name: name, value });
  }

  confirmValue(name: string, val: string) {
    this.dispatch({ type: "confirmValue", name: name, value: val });
  }

  /*
  returns true|false if the field has a value
  if a value is provided, it will also check if the value matches
  */
  hasValue(name: string, value: string | null = null) {
    if (value) return this.state[name].value !== value;
    else return !!this.state[name].value;
  }

  setErrors(name: string, value: string | string[]) {
    if (typeof value === "string") {
      value = [value];
    }

    return this.dispatch({
      type: "writeNewState",
      newState: setFieldErrors(this.state, name, value),
    });
  }

  async trySubmit(
    opts?: {
      beforeHook?: (manager: FieldsManager<T>) => Promise<void | string[]>;
      method?: "post" | "get" | "delete";
    },
  ) {
    /*
    this check prevents multiple submissions, important b/c beforeSubmit funcs
    almost always include async calls to stripe that take an indeterminate amount of time

    if we dont do this, you can create multiple subs by hammering the submit button
    */
    if (this.tryingSubmit) return;

    this.tryingSubmit = true;
    let beforeErrors: string[] | void | undefined;
    if (opts?.beforeHook) beforeErrors = await opts.beforeHook(this);
    const newState = validateAllFields(this.state, beforeErrors);

    if (beforeErrors && beforeErrors.length) {
      this.dispatch({ type: "errors", name: "payment_method", value: beforeErrors });
      this.tryingSubmit = false;
      return;
    }

    if (this.hasErrors(newState)) {
      return this.dispatch({ type: "writeNewState", newState: newState });
    } else {
      const method = opts?.method || "post";
      this.tryingSubmit = false;
      this.submit(this.toFormData(), { replace: true, method });
    }
  }
}

function setFieldErrors<T extends Fieldset>(state: T, name: string, value: string[]) {
  const newState = structuredClone(state);
  newState[name].errors = value;
  return newState;
}

function fieldsetToFormData(fields: Fieldset) {
  const formData = new FormData();
  for (const field in fields) {
    const val = fields[field].value;
    if (val !== undefined && val !== "") {
      formData.append(field, val ? val.toString() : "");
    }
  }
  return formData;
}

function validateAllFields<T extends Fieldset>(
  fields: T,
  beforeErrors?: string[] | undefined | void,
) {
  const newFields = structuredClone(fields);
  for (const field in newFields) {
    if (newFields[field].validation?.required && !newFields[field].value) {
      newFields[field].errors = [`${newFields[field].label} is required`];
    } else if (
      newFields[field].validation?.confirmed &&
      newFields[field].value !== newFields[field].validation?.confirmed.value
    ) {
      newFields[field].errors = [
        `${newFields[field].label} and ${newFields[field].validation?.confirmed.label} must match`,
      ];
    } else {
      if (typeof newFields[field] !== "boolean") newFields[field].errors = [];
    }
  }

  return newFields;
}

export const FormContext = React.createContext<FieldsManager<any> | null>(null);
